import { useCallback, useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/library/card";
import { Button } from "@/components/ui/button";
import CommunicationActivity from "./components/CommunicationActivity";
import StatusActivity from "./components/StatusActivity";
import UpdateActivity from "./components/UpdateActivity";
import SystemActivity from "./components/SystemActivity";
import PaymentActivity from "./components/PaymentActivity";
import DocumentActivity from "./components/DocumentActivity";
import { Separator } from "@/components/ui/separator";
import { useActivityLog } from "@/features/leads/api/useActivityLog";
import { useLeadForm } from "@/features/leads/contexts/FormLeadContext";
import { useChildForm } from "@/features/leads/contexts/ChildProvider";
import { Loader2 } from "lucide-react";

type FilterTab =
  | "All Activity"
  | "Communication"
  | "Status"
  | "Updates"
  | "System"
  | "Payment"
  | "Documents";

const filterTabs: FilterTab[] = [
  "All Activity",
  "Communication",
  "Status",
  "Updates",
  "Payment",
  "Documents",
  "System",
];

export default function ActivityLog() {
  const [activeFilter, setActiveFilter] = useState<FilterTab>("All Activity");

  const { lead } = useLeadForm();
  const { currentChildId } = useChildForm();

  const { logs, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useActivityLog({
      childId: currentChildId,
      leadId: lead?.id,
      category: activeFilter,
    });

  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) observerRef.current.disconnect();
      if (!node || !hasNextPage) return;

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      });
      observerRef.current.observe(node);
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  useEffect(() => {
    return () => observerRef.current?.disconnect();
  }, []);

  const renderActivity = (log: any, index: number) => {
    const isLast = index === logs.length - 1;

    switch (log.category?.toLowerCase()) {
      case "communication":
        return (
          <CommunicationActivity key={log.id} activity={log} isLast={isLast} />
        );
      case "pipeline_event":
        return <StatusActivity key={log.id} activity={log} isLast={isLast} />;
      case "update":
        return <UpdateActivity key={log.id} activity={log} isLast={isLast} />;
      case "system":
        return <SystemActivity key={log.id} activity={log} isLast={isLast} />;
      case "payment":
        return <PaymentActivity key={log.id} activity={log} isLast={isLast} />;
      case "document_event":
        return (
          <DocumentActivity key={log.id} activity={log} isLast={isLast} />
        );
      default:
        return log.isSystem ? (
          <SystemActivity key={log.id} activity={log} isLast={isLast} />
        ) : (
          <StatusActivity key={log.id} activity={log} isLast={isLast} />
        );
    }
  };

  return (
    <div className="min-w-0 w-full p-4">
      <Card className="w-full min-w-0 px-4">
        <div className="flex w-full min-w-0 flex-wrap gap-2 items-center">
          {filterTabs.map((tab) => (
            <Button
              key={tab}
              type="button"
              size="sm"
              variant={activeFilter === tab ? "primary" : "outline"}
              onClick={() => setActiveFilter(tab)}
            >
              {tab}
            </Button>
          ))}
        </div>
        <Separator />
        <CardContent className="px-3">
          <div className="flex flex-col items-start w-full min-w-0">
            {isLoading ? (
              <div className="flex items-center justify-center w-full py-8">
                <Loader2 className="size-5 animate-spin text-[#8a918d]" />
              </div>
            ) : logs.length === 0 ? (
              <p className="text-sm text-[#8a918d] py-8 w-full text-center">
                No activity found
              </p>
            ) : (
              <>
                {logs.map((log: any, index: number) =>
                  renderActivity(log, index)
                )}
                <div ref={sentinelRef} className="h-1 w-full" />
                {isFetchingNextPage && (
                  <div className="flex items-center justify-center w-full py-4">
                    <Loader2 className="size-4 animate-spin text-[#8a918d]" />
                  </div>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
